import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { Product } from '@models';
import { TaxListInfoService } from '@products/tax/services/tax-list-info.service';
import {
  TaxInfoDownloadType,
  TaxListInfoListing,
} from '@products/types/tax-list-info.type';
import { NgxSpinnerService } from 'ngx-spinner';
import { Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { Logger } from '@utils/logger';
import {
  FrkBreakpoint,
  InfoItem,
  ResponsiveService,
} from '@frk/eds-components';
import { TranslateService } from '@shared/translate/translate.service';

const logger = Logger.getLogger('TaxInfoRowComponent');

@Component({
  selector: 'ft-tax-info-row',
  templateUrl: './tax-info-row.component.html',
  styleUrls: ['./tax-info-row.component.scss'],
})
export class TaxInfoRowComponent implements OnInit, OnDestroy, OnChanges {
  /**
   * to check expand and collapse of additional info
   */
  public isExpanded = false;

  public isError = false;

  /**
   * data to render the table
   */
  @Input() taxInfoItem: TaxListInfoListing;

  @Input() expandAll = false;

  mobilePrimaryData: InfoItem[];
  additionalData: InfoItem[];

  private unsubscribe$: Subject<void> = new Subject<void>();
  public isCardView$: Observable<boolean>;

  @Output()
  checkboxToggled: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output()
  print: EventEmitter<TaxListInfoListing> = new EventEmitter<TaxListInfoListing>();

  constructor(
    private responsiveService: ResponsiveService,
    private taxListInfoService: TaxListInfoService,
    private spinner: NgxSpinnerService,
    private translateService: TranslateService,
    private cdr: ChangeDetectorRef
  ) {
    this.isCardView$ = this.responsiveService.isBreakpointOrBelow$(
      FrkBreakpoint.Tablet
    );
  }

  ngOnInit(): void {
    this.spinner.show('taxExemptLoadingSpinner');
    if (this?.taxInfoItem) {
      this.createMobileData();
    }
  }

  ngOnChanges(): void {
    this.isExpanded = this.expandAll;
    if (this.expandAll) {
      this.expandData();
    }
  }

  /**
   * Handle click event when we expand or collapse the accordion
   */
  public toggleExpand(): void {
    this.isExpanded = !this.isExpanded;
    this.expandData();
  }

  private expandData(): void {
    if (this.taxInfoItem.stateFlag && !this.taxInfoItem.isTaxExemptAvailable) {
      this.taxListInfoService
        .getTaxInfoTaxExemptData$({
          fundId: this.taxInfoItem.fundId,
          shareClassCode: this.taxInfoItem.shareClassCode,
          year: Number(this.taxInfoItem.taxYear),
        })
        .pipe(takeUntil(this.unsubscribe$))
        .subscribe(
          (data: Product) => {
            const statesData = data?.shareClasses[0]?.tax?.states;
            this.taxInfoItem.taxExemptData = statesData;
            this.taxInfoItem.isTaxExemptAvailable = true;
            this.cdr.detectChanges();
          },
          (error) => {
            logger.error(`Error in fetching result: ${error}`);
            this.isError = true;
            this.cdr.detectChanges();
          }
        );
    }
  }

  public onToggle(event: boolean): void {
    this.checkboxToggled.emit(event);
  }

  createMobileData() {
    this.mobilePrimaryData = [
      {
        label: this.translateService.instant(
          `products.us-tax-info-page-directusgovernmentobligations`
        ),
        val: this.taxInfoItem.directUSGovernmentObligations,
      },
      {
        label: this.translateService.instant(
          `products.us-tax-info-page-dividendsreceiveddeduction`
        ),
        val: this.taxInfoItem.dividendsReceivedDeduction,
      },
      {
        label: this.translateService.instant(
          `products.us-tax-info-page-foreignsourceincome`
        ),
        val: this.taxInfoItem.foreignSourceIncome,
      },
    ];
    this.additionalData = [
      {
        label: this.translateService.instant(
          `products.us-tax-info-page-foreignsourceincome`
        ),
        val: this.taxInfoItem.foreignSourceIncome,
      },
      {
        label: this.translateService.instant(
          `products.us-tax-info-page-foreignsourcequalifieddividends`
        ),
        val: this.taxInfoItem.foreignSourceQualifiedDividends,
      },
      {
        label: this.translateService.instant(
          `products.us-tax-info-page-directusgovernmentobligations`
        ),
        val: this.taxInfoItem.directUSGovernmentObligations,
      },
      {
        label: this.translateService.instant(
          `products.us-tax-info-page-specifiedpvtactivitybondinterest`
        ),
        val: this.taxInfoItem.specifiedPvtActivityBondInterest,
      },
      {
        label: this.translateService.instant(
          `products.us-tax-info-page-dividendsreceiveddeduction`
        ),
        val: this.taxInfoItem.dividendsReceivedDeduction,
      },
      {
        label: this.translateService.instant(
          `products.us-tax-info-page-section163`
        ),
        val: this.taxInfoItem.section163,
      },
      {
        label: this.translateService.instant(
          `products.us-tax-info-page-indianaresidenttaxableincome`
        ),
        val: this.taxInfoItem.indianaResidentTaxableIncome,
      },
      {
        label: this.translateService.instant(
          `products.us-tax-info-page-utahresidenttaxableincome`
        ),
        val: this.taxInfoItem.utahResidentTaxableIncome,
      },
    ];
  }
  /**
   * to download the selected fund from the accordion
   * @param event triggered event
   */
  onDownloadExcel(event: MouseEvent) {
    event?.preventDefault();
    this.taxListInfoService.handleDownloadAction(
      TaxInfoDownloadType.SINGLE,
      this.taxInfoItem.fundId
    );
  }

  /**
   * to print the selected fund from the accordion
   * TODO : will be completed in print functionality jira
   * @param event triggered event
   */
  onPrintExcel(event: MouseEvent) {
    event?.preventDefault();
    this.print.emit(this.taxInfoItem);
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }
}
